import React, { useEffect, useState } from 'react';
import JSZip from 'jszip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, FolderOpen, File, Download } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface ZipViewerProps {
  url: string;
}

interface ZipEntry {
  name: string;
  size: number;
  isFolder: boolean;
  path: string;
}

export const ZipViewer = ({ url }: ZipViewerProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [entries, setEntries] = useState<ZipEntry[]>([]);
  const [totalSize, setTotalSize] = useState(0);

  useEffect(() => {
    const loadZip = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(url);
        if (!response.ok) throw new Error('Error al cargar el archivo');

        const arrayBuffer = await response.arrayBuffer();
        const zip = await JSZip.loadAsync(arrayBuffer);

        const fileList: ZipEntry[] = [];
        let total = 0;

        const filePromises: Promise<void>[] = [];
        
        zip.forEach((relativePath, file) => {
          const isFolder = file.dir;
          const parts = relativePath.split('/');
          const name = isFolder ? parts[parts.length - 2] || relativePath : parts[parts.length - 1];

          filePromises.push(
            (async () => {
              let size = 0;
              if (!isFolder) {
                // Obtener el tamaño real del archivo
                const content = await file.async('uint8array');
                size = content.length;
                total += size;
              }

              fileList.push({
                name,
                size,
                isFolder,
                path: relativePath
              });
            })()
          );
        });

        await Promise.all(filePromises);

        // Ordenar: carpetas primero, luego archivos
        fileList.sort((a, b) => {
          if (a.isFolder && !b.isFolder) return -1;
          if (!a.isFolder && b.isFolder) return 1;
          return a.name.localeCompare(b.name);
        });

        setEntries(fileList);
        setTotalSize(total);
        setLoading(false);
      } catch (err) {
        console.error('Error cargando ZIP:', err);
        setError(err instanceof Error ? err.message : 'Error al cargar el archivo');
        setLoading(false);
      }
    };

    loadZip();
  }, [url]);

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  const fileCount = entries.filter(e => !e.isFolder).length;
  const folderCount = entries.filter(e => e.isFolder).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FolderOpen className="h-5 w-5 text-viable-blue" />
          <span className="font-medium">Contenido del archivo ZIP</span>
        </div>
        
        <Button asChild variant="outline" size="sm">
          <a href={url} download>
            <Download className="h-4 w-4 mr-2" />
            Descargar ZIP
          </a>
        </Button>
      </div>

      <div className="flex gap-4 text-sm">
        <Badge variant="secondary">
          {fileCount} archivo{fileCount !== 1 ? 's' : ''}
        </Badge>
        {folderCount > 0 && (
          <Badge variant="secondary">
            {folderCount} carpeta{folderCount !== 1 ? 's' : ''}
          </Badge>
        )}
        <Badge variant="secondary">
          Tamaño total: {formatBytes(totalSize)}
        </Badge>
      </div>

      <div className="border rounded-md overflow-auto max-h-[500px]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Ruta</TableHead>
              <TableHead className="text-right">Tamaño</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground">
                  El archivo ZIP está vacío
                </TableCell>
              </TableRow>
            ) : (
              entries.map((entry, idx) => (
                <TableRow key={idx}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {entry.isFolder ? (
                        <FolderOpen className="h-4 w-4 text-viable-blue" />
                      ) : (
                        <File className="h-4 w-4 text-muted-foreground" />
                      )}
                      {entry.name}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {entry.path}
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {entry.isFolder ? '-' : formatBytes(entry.size)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
